import React, { useEffect, useState } from 'react'
import moment from 'moment'

import { t } from '@/i18n'
import {
  DealDeprecatedHistoryNode,
  useDealHistoryQuery,
} from '@/apollo/graphql'
import LoadingIndicator from '@/assets/images/loading_indicator.gif'
import { IPageInfo } from '@/types'
import { getFetchMoreStatus } from '@/utils'
import { InfiniteLoader } from '@/components'

interface Props {
  id: string
}

const pageSize = 20

const initialPageInfo: IPageInfo = {
  hasNextPage: false,
  offset: 0,
  fetchMoreStatus: 'loading',
}

const History: React.FC<Props> = ({ id }) => {
  const [updates, setUpdates] = useState<Partial<DealDeprecatedHistoryNode>[]>(
    [],
  )
  const [pageInfo, setPageInfo] = useState<IPageInfo>(initialPageInfo)

  const { loading, networkStatus } = useDealHistoryQuery({
    fetchPolicy: 'cache-first',
    variables: {
      id,
      offset: pageInfo?.offset || 0,
      first: pageSize,
    },
    onCompleted: res => {
      if (res.deal?.dealdeprecatedhistorySet?.edges) {
        const d: Partial<DealDeprecatedHistoryNode>[] = []
        res.deal.dealdeprecatedhistorySet.edges.forEach(e => {
          // @ts-ignore
          if (e?.node) d.push(e.node)
        })
        setUpdates(prev => [...prev, ...d])
        setPageInfo(prev => ({
          ...prev,
          hasNextPage: res.deal?.dealdeprecatedhistorySet?.pageInfo.hasNextPage,
        }))
      }
    },
  })

  useEffect(() => {
    setPageInfo(prev => ({
      ...prev,
      fetchMoreStatus: getFetchMoreStatus({
        networkStatus,
        hasNextPage: prev?.hasNextPage,
        withForce: true,
      }),
    }))
  }, [networkStatus])

  return (
    <div>
      <InfiniteLoader
        fetchMoreStatus={pageInfo.fetchMoreStatus}
        onFetchMore={() =>
          setPageInfo(prev => ({ ...prev, offset: updates.length }))
        }
      >
        <table>
          <thead>
            <tr>
              <th>{t('field_name')}</th>
              <th>{t('old_value')}</th>
              <th>{t('new_value')}</th>
              <th>{t('date_and_time')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr className="history-loading">
                <td colSpan={4}>
                  <img src={LoadingIndicator} />
                </td>
              </tr>
            )}
            {!loading && !updates.length && (
              <tr className="history-empty">
                <td colSpan={4}>{t('noData')}</td>
              </tr>
            )}
            {updates.map((datum, index) => (
              <tr key={index}>
                <td>{t(`${datum.fieldName}`)}</td>
                <td>{datum.oldValue}</td>
                <td>{datum.newValue}</td>
                <td>
                  {datum.updateTime
                    ? moment(datum.updateTime).format('ll HH:mm:ss')
                    : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </InfiniteLoader>
    </div>
  )
}

export default History
